import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';

export type ScrollRestoreState = {
  scrollRestore?: { swimlaneId: string; elementIndex: number };
  verticalScroll?: number;
  scrollToTop?: boolean;
};

/**
 * Link state for top-level navigation. A Link to the current URL navigates as a replace, which otherwise keeps
 * the page where it is.
 */
export const scrollToTopState: ScrollRestoreState = { scrollToTop: true };

const STORAGE_KEY = 'scroll-positions';
const MAX_STORED_POSITIONS = 100;
const RESTORE_TIMEOUT_MS = 3000;
const RESTORE_CHECK_INTERVAL_MS = 150;
const RESTORE_TOLERANCE_PX = 10;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

// Keyed by entry, and persisted for the tab since location.key survives a reload
const savedPositions = readSavedPositions();

// Every fresh document load without history state gets the key "default", so the URL tells those entries apart
const getEntryId = (key: string, path: string) => `${key} ${path}`;

function readSavedPositions() {
  try {
    return new Map<string, number>(JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]'));
  } catch {
    return new Map<string, number>();
  }
}

const persistSavedPositions = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...savedPositions].slice(-MAX_STORED_POSITIONS)));
  } catch {
    // storage unavailable, positions just won't survive a reload
  }
};

// Only a reload or a back/forward into the app resumes where the document was left; a new visit starts over
const isDocumentResumed = () => {
  const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  return entry?.type === 'reload' || entry?.type === 'back_forward';
};

// The target of a restore that has not landed yet, left in place when a navigation interrupts it
let pendingRestore: number | undefined;

// The page we return to usually renders before its content has loaded, so keep retrying until it is tall enough
const restoreScrollPosition = (target: number) => {
  const deadline = Date.now() + RESTORE_TIMEOUT_MS;
  let timer: number | undefined;
  pendingRestore = target;

  const stop = () => {
    window.clearTimeout(timer);
    USER_SCROLL_EVENTS.forEach(event => window.removeEventListener(event, finish));
  };

  const finish = () => {
    pendingRestore = undefined;
    stop();
  };

  const attempt = () => {
    window.scrollTo(0, target);
    // content that is still settling can move the page right after it landed, so verify after a beat
    timer = window.setTimeout(() => {
      if (Math.abs(window.scrollY - target) > RESTORE_TOLERANCE_PX && Date.now() < deadline) {
        attempt();
      } else {
        finish();
      }
    }, RESTORE_CHECK_INTERVAL_MS);
  };

  USER_SCROLL_EVENTS.forEach(event => window.addEventListener(event, finish, { passive: true }));
  attempt();

  return stop;
};

/**
 * Back/forward restores the offset the entry was left at, a new entry starts at the top,
 * and a replace keeps the page where it is.
 */
export const useRestoreScrollOnNavigation = () => {
  const { key, pathname, search, state } = useLocation();
  const navigationType = useNavigationType();
  const entryId = getEntryId(key, pathname + search);
  const currentEntryId = useRef(entryId);
  const isInitialRender = useRef(true);

  // The browser's own restoration would re-apply its saved offset after ours on back-navigation and override it
  useLayoutEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    const savePosition = () => {
      // offsets seen while a restore is still short of its target are not where the user left the page
      if (pendingRestore === undefined) {
        savedPositions.set(currentEntryId.current, window.scrollY);
      }
    };
    window.addEventListener('scroll', savePosition, { passive: true });
    window.addEventListener('pagehide', persistSavedPositions);
    return () => {
      window.removeEventListener('scroll', savePosition);
      window.removeEventListener('pagehide', persistSavedPositions);
    };
  }, []);

  useLayoutEffect(() => {
    currentEntryId.current = entryId;
    const restoreState = state as ScrollRestoreState | null;
    const interruptedRestore = pendingRestore;
    pendingRestore = undefined;

    if (isInitialRender.current) {
      isInitialRender.current = false;
      // Fresh loads share the key "default", so a stale offset from an earlier visit to the URL must be dropped
      if (!isDocumentResumed()) {
        savedPositions.delete(entryId);
      }
    }

    if (navigationType === NavigationType.Replace && !restoreState?.scrollToTop) {
      // A replace gets a new key, so carry the offset over to it. A page that rewrites its URL while it is
      // still loading, like the TV guide dropping a stale date param, must not cut a restore short
      if (interruptedRestore !== undefined) {
        savedPositions.set(entryId, interruptedRestore);
        return restoreScrollPosition(interruptedRestore);
      }
      savedPositions.set(entryId, window.scrollY);
      return;
    }

    // The swimlane restore point is replaced and pushed past in one render, so its key is never
    // seen here and the offset has to come from its state
    const target =
      navigationType === NavigationType.Pop ? savedPositions.get(entryId) ?? restoreState?.verticalScroll : undefined;

    if (target === undefined) {
      window.scrollTo(0, 0);
      return;
    }
    return restoreScrollPosition(target);
  }, [entryId, navigationType, state]);
};

export function useSetScrollRestoration() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const setRestoreScrollPoint = useCallback(
    (swimlaneId: string, elementIndex: number) => {
      const state: ScrollRestoreState = {
        verticalScroll: window.scrollY,
        scrollRestore: { swimlaneId, elementIndex: elementIndex },
      };
      navigate(pathname + search, { state, replace: true });
    },
    [navigate, pathname, search]
  );

  return { setRestoreScrollPoint };
}
